<template>
  <footer class="footer">
    <div class="footer-content">
      <!-- Main Footer Sections -->
      <div class="footer-sections">
        <!-- Quick Links -->
        <div class="footer-section">
          <h3>Quick Links</h3>
          <ul class="footer-links">
            <li><router-link to="/">Home</router-link></li>
            <li><router-link to="/sitemap">Sitemap</router-link></li>
            <li><router-link to="/products">All Products</router-link></li>
            <li><router-link to="/favorites">My Favorites</router-link></li>
          </ul>
        </div>

        <!-- Main Categories -->
        <div class="footer-section">
          <h3>Categories</h3>
          <ul class="footer-links">
            <li><router-link to="/category/electronics">Electronics</router-link></li>
            <li><router-link to="/category/home">Home & Garden</router-link></li>
            <li><router-link to="/category/fashion">Fashion</router-link></li>
            <li><router-link to="/category/beauty">Beauty</router-link></li>
            <li><router-link to="/category/outdoor">Outdoor</router-link></li>
          </ul>
        </div>

        <!-- Legal & Support -->
        <div class="footer-section">
          <h3>Legal & Support</h3>
          <ul class="footer-links">
            <li><router-link to="/terms">Terms of Service</router-link></li>
            <li><router-link to="/privacy">Privacy Policy</router-link></li>
            <li><router-link to="/affiliate-disclosure">Affiliate Disclosure</router-link></li>
            <li><a href="mailto:takefavcom@gmail.com" target="_blank">Contact us</a></li>
          </ul>
        </div>
      </div>

      <!-- Affiliate Disclosure -->
      <div class="affiliate-disclosure">
        <div class="disclosure-content">
          <h4>Affiliate Disclosure</h4>
          <p>
            TakeFav is a participant in the Amazon Services LLC Associates Program, eBay Partner
            Network, and AliExpress Affiliate Program. As an affiliate, we earn commissions from
            qualifying purchases made through our links. This helps support our site and allows us
            to continue providing you with valuable product recommendations. All opinions and
            recommendations are our own and not influenced by affiliate partnerships.
          </p>
        </div>
        <div class="affiliate-partners">
          <h3>Our Trusted Partners</h3>
          <div class="partner-logos">
            <div class="partner-logo">
              <a
                href="https://www.amazon.com/?tag=dalins209-20"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img :src="getProxiedImageUrl(amazonLogo)" alt="Amazon" />
              </a>
            </div>
            <div class="partner-logo">
              <a
                href="https://www.ebay.com/?_trksid=p5339111533"
                target="_blank"
                rel="noopener sponsored"
              >
                <img :src="getProxiedImageUrl(ebayLogo)" alt="eBay" />
              </a>
            </div>
            <div class="partner-logo">
              <a
                href="https://s.click.aliexpress.com/e/_TakeFav"
                target="_blank"
                rel="noopener sponsored"
              >
                <img :src="getProxiedImageUrl(aliexpressLogo)" alt="AliExpress" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="footer-bottom">
        <div class="footer-bottom-content">
          <div class="copyright">
            <p>&copy; {{ currentYear }} TakeFav. All rights reserved.</p>
          </div>
          <div class="footer-bottom-links">
            <router-link to="/terms">Terms</router-link>
            <router-link to="/privacy">Privacy</router-link>
            <router-link to="/cookies">Cookies</router-link>
            <router-link to="/accessibility">Accessibility</router-link>
          </div>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { computed } from 'vue'
defineOptions({ name: 'AppFooter' })

const currentYear = computed(() => new Date().getFullYear())

// Import centralized environment management
import { getImageUrl } from '../utils/environment'
import { getProxiedImageUrl } from '../utils/imageProxy'

// Use centralized image URLs as computed properties to ensure they're evaluated after mounting
const amazonLogo = computed(() => getImageUrl('logos/available_at_amazon_150w.png'))
const ebayLogo = computed(() => getImageUrl('logos/ebay_logo.png'))
const aliexpressLogo = computed(() => getImageUrl('logos/aliexpress_logo.svg'))
</script>

<style lang="scss" scoped>
// Variables
$primary-gradient: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
$accent-color: #ffd700;
$text-light: #b3d4fc;
$text-white: #fff;
$border-light: rgba(255, 255, 255, 0.2);
$bg-light: rgba(255, 255, 255, 0.1);
$bg-disclosure: rgba(255, 255, 255, 0.05);

.footer {
  background: $primary-gradient;
  color: $text-white;
  margin-top: 80px;

  &-content {
    margin: 0 auto;
    padding: 40px 20px 20px;
  }

  &-sections {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 40px;
    max-width: 1200px;
    margin: 40px auto;
  }

  &-section {
    h3 {
      font-size: 18px;
      font-weight: 600;
      margin-bottom: 20px;
      color: $text-white;
      position: relative;

      &::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 40px;
        height: 3px;
        background: $accent-color;
        border-radius: 2px;
      }
    }

    p {
      line-height: 1.6;
      color: $text-light;
      margin-bottom: 20px;
    }
  }

  &-links {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      margin-bottom: 12px;
    }

    a {
      color: $text-light;
      text-decoration: none;
      transition: color 0.2s;
      font-size: 14px;

      &:hover {
        color: $accent-color;
      }
    }
  }

  &-bottom {
    border-top: 1px solid $border-light;
    padding-top: 20px;

    &-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 20px;
    }

    &-links {
      display: flex;
      gap: 20px;

      a {
        color: $text-light;
        text-decoration: none;
        font-size: 14px;
        transition: color 0.2s;

        &:hover {
          color: $accent-color;
        }
      }
    }
  }
}

.affiliate {
  &-disclosure {
    padding: 25px;
    background: $bg-disclosure;
    border-radius: 8px;
    border-left: 4px solid $accent-color;
    display: flex;
    gap: 40px;
    max-width: 1600px;
    margin: 0 auto 40px;

    > div {
      flex: 1;
    }
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 20px;
    }
  }

  &-partners {
    text-align: center;
    padding: 30px;
    background: $bg-light;
    border-radius: 12px;

    h3 {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 25px;
      color: $text-white;
    }

    .partner-logos {
      img {
        width: 120px;
        height: auto;
      }
    }
  }
}

.disclosure {
  &-content {
    h4 {
      font-size: 16px;
      font-weight: 600;
      margin-bottom: 15px;
      color: $accent-color;
    }

    p {
      font-size: 14px;
      line-height: 1.6;
      color: $text-light;
      margin: 0;
    }
  }
}

.partner {
  &-logos {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 40px;
    flex-wrap: wrap;
  }

  &-logo {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 20px;
    border-radius: 8px;
    transition: transform 0.2s;

    &:hover {
      transform: translateY(-4px);
    }

    img {
      width: 75px;
      height: 30px;
      object-fit: contain;
    }

    span {
      font-size: 14px;
      font-weight: 600;
      color: $text-white;
    }
  }
}

.copyright {
  p {
    color: $text-light;
    font-size: 14px;
    margin: 0;
  }
}

// Responsive Design
@media (max-width: 768px) {
  .footer {
    &-content {
      padding: 30px 15px 15px;
    }

    &-sections {
      grid-template-columns: repeat(auto-fit, minmax(155px, 1fr));
      gap: 30px;
      margin: 0 10px 40px;
    }

    &-bottom-content {
      flex-direction: column;
      text-align: center;
    }

    &-bottom-links {
      justify-content: center;
    }
  }

  .partner {
    &-logos {
      gap: 20px;
    }

    &-logo {
      padding: 15px;

      img {
        width: 50px;
        height: 35px;
      }
    }
  }
}

@media (max-width: 480px) {
  .partner-logos {
    flex-direction: column;
    gap: 15px;
  }
}
</style>
